import React, { useState, useEffect } from 'react';
import { PrivateAxios } from '../../environment/AxiosInstance';
import DataTable from 'react-data-table-component';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Loader from '../../loader/Loader';
import { toast } from 'react-toastify';
import './WhatsappLogs.css';

const WhatsappLogs = () => {
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [companies, setCompanies] = useState([]);
    
    // Filter states
    const [selectedCompany, setSelectedCompany] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [serviceFilter, setServiceFilter] = useState('');

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    useEffect(() => {
        fetchLogs();
        fetchCompanies();
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [selectedCompany, startDate, endDate, searchText, statusFilter, serviceFilter, currentPage, perPage]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (selectedCompany) params.append('company_id', selectedCompany);
            if (startDate) params.append('start_date', moment(startDate).format('YYYY-MM-DD'));
            if (endDate) params.append('end_date', moment(endDate).format('YYYY-MM-DD'));
            if (statusFilter) params.append('status', statusFilter);
            if (serviceFilter) params.append('service', serviceFilter);
            if (searchText) params.append('search', searchText);
            params.append('limit', perPage);
            params.append('page', currentPage);

            const response = await PrivateAxios.get(`/whatsapp-logs/get-logs?${params.toString()}`);
            setLogs(response.data.data || []);
        } catch (error) {
            console.error('Error fetching logs:', error);
            toast.error('Failed to fetch WhatsApp logs');
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await PrivateAxios.get('/whatsapp-logs/get-companies');
            setCompanies(response.data.data || []);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    };

    const clearFilters = () => {
        setSelectedCompany('');
        setStartDate(null);
        setEndDate(null);
        setSearchText('');
        setStatusFilter('');
        setServiceFilter('');
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            'pending': 'warning',
            'sent': 'success',
            'delivered': 'info',
            'failed': 'danger'
        };
        return (
            <span className={`badge bg-${statusColors[status] || 'secondary'}`}>
                {status?.toUpperCase()}
            </span>
        );
    };

    const getServiceBadge = (service) => {
        const serviceColors = {
            'greenapi': 'success',
            'maytapi': 'primary',
            'gupshup': 'info',
            'meta': 'dark'
        };
        return (
            <span className={`badge bg-${serviceColors[service] || 'secondary'}`}>
                {service?.toUpperCase()}
            </span>
        );
    };

    const columns = [
        {
            name: 'Date/Time',
            selector: row => row.created_at,
            sortable: true,
            width: '150px',
            cell: row => (
                <div className="text-nowrap">
                    <div>{moment(row.created_at).format('DD-MM-YYYY')}</div>
                    <small className="text-muted">{moment(row.created_at).format('hh:mm A')}</small>
                </div>
            )
        },
        {
            name: 'Company',
            selector: row => row.company_name,
            sortable: true,
            width: '150px',
            cell: row => (
                <div className="text-truncate" title={row.company_name}>
                    {row.company_name}
                </div>
            )
        },
        {
            name: 'Sender',
            selector: row => row.sender_name,
            sortable: true,
            width: '120px',
            cell: row => (
                <div className="text-truncate" title={row.sender_name}>
                    {row.sender_name || 'System'}
                </div>
            )
        },
        {
            name: 'Receiver',
            selector: row => row.receiver_name,
            sortable: true,
            width: '150px',
            cell: row => (
                <div>
                    <div className="text-truncate" title={row.receiver_name}>
                        {row.receiver_name}
                    </div>
                    <small className="text-muted">+{row.receiver_isd} {row.receiver_phone}</small>
                </div>
            )
        },
        {
            name: 'Message',
            selector: row => row.message_content,
            width: '200px',
            cell: row => (
                <div className="text-truncate" title={row.message_content}>
                    {row.message_content}
                </div>
            )
        },
        {
            name: 'Type',
            selector: row => row.notification_type,
            sortable: true,
            width: '120px',
            cell: row => (
                <span className="badge bg-secondary">
                    {row.notification_type?.toUpperCase()}
                </span>
            )
        },
        {
            name: 'Service',
            selector: row => row.whatsapp_service,
            sortable: true,
            width: '120px',
            cell: row => getServiceBadge(row.whatsapp_service)
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            width: '120px',
            cell: row => getStatusBadge(row.status)
        },
        {
            name: 'Actions',
            width: '100px',
            cell: row => (
                <button 
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => viewDetails(row)}
                    title="View Details"
                >
                    <i className="fas fa-eye"></i>
                </button>
            )
        }
    ];

    const viewDetails = (row) => {
        // You can implement a modal to show full details
        console.log('View details for:', row);
    };

    return (
        <div className="whatsapp-logs-container">
            {loading && <Loader />}
            
            <div className="gthh-controller-bar">
                <ul className="gth-controller-view-block">
                    <li>
                        <div className="gth-controller-view-item active">
                            <i className="fab fa-whatsapp me-2" />
                            WhatsApp Message Logs
                        </div>
                    </li>
                </ul>
                <div className="gth-controller-action-block">
                    <button 
                        className="btn btn-outline-success btn-sm"
                        onClick={fetchLogs}
                        title="Refresh Logs"
                    >
                        <i className="fas fa-sync-alt me-1"></i>
                        Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="card whatsapp-filters-card">
                <div className="card-header">
                    <h5 className="mb-0">
                        <i className="fas fa-filter me-2"></i>
                        Filters
                    </h5>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-lg-3 col-md-4 col-sm-6">
                            <div className="filter-form-group">
                                <label className="form-label">Company</label>
                                <select 
                                    className="form-select"
                                    value={selectedCompany}
                                    onChange={(e) => setSelectedCompany(e.target.value)}
                                >
                                    <option value="">All Companies</option>
                                    {companies.map(company => (
                                        <option key={company.company_id} value={company.company_id}>
                                            {company.company_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                        <div className="col-lg-2 col-md-3 col-sm-6">
                            <div className="filter-form-group">
                                <label className="form-label">Start Date</label>
                                <DatePicker
                                    selected={startDate}
                                    onChange={setStartDate}
                                    className="form-control"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Start Date"
                                    maxDate={new Date()}
                                />
                            </div>
                        </div>
                        
                        <div className="col-lg-2 col-md-3 col-sm-6">
                            <div className="filter-form-group">
                                <label className="form-label">End Date</label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={setEndDate}
                                    className="form-control"
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="End Date"
                                    maxDate={new Date()}
                                    minDate={startDate}
                                />
                            </div>
                        </div>
                        
                        <div className="col-lg-2 col-md-3 col-sm-6">
                            <div className="filter-form-group">
                                <label className="form-label">Status</label>
                                <select 
                                    className="form-select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="sent">Sent</option>
                                    <option value="delivered">Delivered</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </div>
                        </div>
                        
                        <div className="col-lg-2 col-md-3 col-sm-6">
                            <div className="filter-form-group">
                                <label className="form-label">Service</label>
                                <select 
                                    className="form-select"
                                    value={serviceFilter}
                                    onChange={(e) => setServiceFilter(e.target.value)}
                                >
                                    <option value="">All Services</option>
                                    <option value="greenapi">GreenAPI</option>
                                    <option value="maytapi">Maytapi</option>
                                    <option value="gupshup">GupShup</option>
                                    <option value="meta">Meta</option>
                                </select>
                            </div>
                        </div>
                        
                        <div className="col-lg-1 col-md-2 col-sm-12 d-flex align-items-end justify-content-center">
                            <button 
                                className="btn btn-outline-secondary"
                                onClick={clearFilters}
                                title="Clear All Filters"
                                style={{ minWidth: '40px', maxWidth: '100%' }}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                    
                    <div className="row mt-3">
                        <div className="col-lg-6 col-md-8">
                            <div className="filter-form-group">
                                <label className="form-label">Search</label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <i className="fas fa-search"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by phone, name, or message..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                    {searchText && (
                                        <button 
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={() => setSearchText('')}
                                            title="Clear Search"
                                        >
                                            <i className="fas fa-times"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-lg-6 col-md-4 d-flex align-items-end justify-content-end">
                            <div className="filter-actions">
                                <div className="btn-group" role="group">
                                    <button 
                                        className="btn btn-primary"
                                        onClick={fetchLogs}
                                        title="Apply Filters"
                                    >
                                        <i className="fas fa-filter me-1"></i>
                                        Apply Filters
                                    </button>
                                    <button 
                                        className="btn btn-outline-success"
                                        onClick={fetchLogs}
                                        title="Refresh Data"
                                    >
                                        <i className="fas fa-sync-alt"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Data Table */}
            <div className="card whatsapp-table-card">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mb-0">
                            <i className="fas fa-list me-2"></i>
                            WhatsApp Message Logs
                        </h5>
                        <small className="text-muted">
                            {logs.length} records found
                            {(selectedCompany || startDate || endDate || statusFilter || serviceFilter || searchText) && 
                                <span className="ms-2">
                                    <i className="fas fa-filter me-1"></i>
                                    Filtered results
                                </span>
                            }
                        </small>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <div className="d-flex gap-2">
                            <span className="badge bg-success">
                                <i className="fas fa-check me-1"></i>
                                Sent: {logs.filter(log => log.status === 'sent').length}
                            </span>
                            <span className="badge bg-warning">
                                <i className="fas fa-clock me-1"></i>
                                Pending: {logs.filter(log => log.status === 'pending').length}
                            </span>
                            <span className="badge bg-danger">
                                <i className="fas fa-times me-1"></i>
                                Failed: {logs.filter(log => log.status === 'failed').length}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="card-body p-0">
                    <DataTable
                        columns={columns}
                        data={logs}
                        pagination
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        onChangeRowsPerPage={setPerPage}
                        onChangePage={setCurrentPage}
                        sortable
                        responsive={false}
                        striped
                        highlightOnHover
                        customStyles={{
                            table: {
                                style: {
                                    minWidth: '1400px',
                                },
                            },
                            headCells: {
                                style: {
                                    paddingLeft: '20px',
                                    paddingRight: '20px',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    color: '#495057',
                                    backgroundColor: '#f8f9fa',
                                    borderRight: '1px solid #dee2e6',
                                },
                            },
                            cells: {
                                style: {
                                    paddingLeft: '20px',
                                    paddingRight: '20px',
                                    fontSize: '13px',
                                    borderRight: '1px solid #f1f3f4',
                                },
                            },
                        }}
                        noDataComponent={
                            <div className="text-center py-5">
                                <i className="fab fa-whatsapp fa-4x text-muted mb-3"></i>
                                <h5 className="text-muted">No WhatsApp logs found</h5>
                                <p className="text-muted mb-3">
                                    {(selectedCompany || startDate || endDate || statusFilter || serviceFilter || searchText) 
                                        ? "Try adjusting your filters to see more results"
                                        : "No messages have been sent yet"
                                    }
                                </p>
                                {(selectedCompany || startDate || endDate || statusFilter || serviceFilter || searchText) && (
                                    <button 
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={clearFilters}
                                    >
                                        <i className="fas fa-times me-1"></i>
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default WhatsappLogs;











